import { EnvironmentProviders, makeEnvironmentProviders, provideAppInitializer, provideEnvironmentInitializer } from '@angular/core';
import { abIfExpField, abIfExpSection } from '@df/module-ab/cms2/ab-cms2';
import { surviveTreeShaking } from '@df/ng/survive-tree-shaking';
import { NICmsContentComponent } from '@icms/core/content';
import { ICmsContentService } from '@icms/core/content/icms-content.service';

// CMS components
export function provideCmsComponents(): EnvironmentProviders {
    return makeEnvironmentProviders([
        provideAppInitializer(() => {
            ICmsContentService.registerComponentMetaPatch((meta: NICmsContentComponent.IMeta) => {
                // add AB test fields into CMS block components
                if (meta.block) {
                    const firstStyleFieldIndex = meta.fields.findIndex(field => field.key.startsWith('styles.')) || meta.fields.length - 1;
                    meta.fields.splice(firstStyleFieldIndex, 0, abIfExpSection, abIfExpField);
                }
            });
        }),
        provideEnvironmentInitializer(() => {
            // Dynamic imports for all components with webpack chunk names
            import(
                /* webpackChunkName: "access-restrictions-modal" */ '../catalog/ui/page/access/access-restrictions-modal.component'
            ).then(c => surviveTreeShaking(c.AccessRestrictionsModalComponent));

            import(/* webpackChunkName: "back-in-stock-modal" */ '../catalog/ui/product/back-in-stock/back-in-stock-modal.component').then(
                c => surviveTreeShaking(c.BackInStockModalComponent)
            );

            import(/* webpackChunkName: "banner-reviews" */ '../catalog/ui/page/component/banner-reviews/banner-reviews.component').then(
                c => surviveTreeShaking(c.BannerReviewsComponent)
            );

            import(
                /* webpackChunkName: "checkout-account-new-cms" */ '../checkout/account/new/cms/checkout-account-new-cms.component'
            ).then(c => surviveTreeShaking(c.CheckoutAccountNewCmsComponent));

            import(/* webpackChunkName: "basket-add-notice" */ '../basket/basket-add-notice.component').then(c =>
                surviveTreeShaking(c.BasketAddNoticeComponent)
            );

            import(/* webpackChunkName: "basket-add-notice-modal" */ '../basket/basket-add-notice-modal.component').then(c =>
                surviveTreeShaking(c.BasketAddNoticeModalComponent)
            );

            import(
                /* webpackChunkName: "basket-straps-upsell-carousel" */ '../basket/straps-upsell-carousel/basket-straps-upsell-carousel.component'
            ).then(c => surviveTreeShaking(c.BasketStrapsUpsellCarouselComponent));

            import(/* webpackChunkName: "basket-upsell-carousel" */ '../basket/upsell-carousel/basket-upsell-carousel.component').then(c =>
                surviveTreeShaking(c.BasketUpsellCarouselComponent)
            );

            import(/* webpackChunkName: "checkbox-required-validator" */ '@df/ui/form/checkbox/checkbox-required-validator.directive').then(
                c => surviveTreeShaking(c.CheckboxRequiredValidatorDirective)
            );

            import(/* webpackChunkName: "checkout-address-model" */ '../checkout/checkout-address-model.component').then(c =>
                surviveTreeShaking(c.CheckoutAddressModelComponent)
            );

            import(/* webpackChunkName: "checkout-minibasket-modal" */ '../checkout/checkout-minibasket-modal.component').then(c =>
                surviveTreeShaking(c.CheckoutMinibasketModalComponent)
            );

            import(/* webpackChunkName: "checkout-minibasket-modal-open" */ '../checkout/checkout-minibasket-modal-open.directive').then(
                c => surviveTreeShaking(c.CheckoutMinibasketModalOpenDirective)
            );

            import(
                /* webpackChunkName: "cms-blog-component-image-with-copy" */ './blog/component/image-with-copy/cms-blog-component-image-with-copy.component'
            ).then(c => {
                surviveTreeShaking(c.CmsBlogComponentImageWithCopyComponent);
            });

            import(
                /* webpackChunkName: "cms-blog-component-image-with-overlapping-copy" */ './blog/component/image-with-overlapping-copy/cms-blog-component-image-with-overlapping-copy.component'
            ).then(c => {
                surviveTreeShaking(c.CmsBlogComponentImageWithOverlappingCopyComponent);
            });

            import(
                /* webpackChunkName: "cms-blog-component-listing" */ './blog/component/listing/cms-blog-component-listing.component'
            ).then(c => {
                surviveTreeShaking(c.CmsBlogComponentListingComponent);
            });

            import(
                /* webpackChunkName: "cms-blog-component-listing-v2" */ './blog/component/listing-v2/cms-blog-component-listing-v2.component'
            ).then(c => {
                surviveTreeShaking(c.CmsBlogComponentListingV2Component);
            });

            import(/* webpackChunkName: "cms-blog-post-view" */ './blog/post/view/cms-blog-post-view.component').then(c =>
                surviveTreeShaking(c.CmsBlogPostViewComponent)
            );

            import(/* webpackChunkName: "cms-blog-root-view" */ './blog/root/view/cms-blog-root-view.component').then(c =>
                surviveTreeShaking(c.CmsBlogRootViewComponent)
            );

            import(/* webpackChunkName: "cms-component-carousel" */ './component/carousel/cms-component-carousel.component').then(c =>
                surviveTreeShaking(c.CmsComponentCarouselComponent)
            );

            import(
                /* webpackChunkName: "cms-component-content-block" */ './component/content-block/cms-component-content-block.component'
            ).then(c => surviveTreeShaking(c.CmsComponentContentBlockComponent));

            import(/* webpackChunkName: "cms-component-custom-form" */ './component/custom-form/cms-component-custom-form.component').then(
                c => surviveTreeShaking(c.CmsComponentCustomFormComponent)
            );

            import(/* webpackChunkName: "cms-component-hero-image" */ './component/hero-image/cms-component-hero-image.component').then(c =>
                surviveTreeShaking(c.CmsComponentHeroImageComponent)
            );

            import(/* webpackChunkName: "cms-component-html" */ './component/html/cms-component-html.component').then(c =>
                surviveTreeShaking(c.CmsComponentHtmlComponent)
            );

            import(/* webpackChunkName: "cms-component-image" */ './component/image/cms-component-image.component').then(c =>
                surviveTreeShaking(c.CmsComponentImageComponent)
            );

            import(/* webpackChunkName: "cms-component-links" */ './component/links/cms-component-links.component').then(c =>
                surviveTreeShaking(c.CmsComponentLinksComponent)
            );

            import(/* webpackChunkName: "cms-component-listing" */ './component/listing/cms-component-listing.component').then(c =>
                surviveTreeShaking(c.CmsComponentListingComponent)
            );

            import(/* webpackChunkName: "cms-component-menu" */ './component/menu/cms-component-menu.component').then(c =>
                surviveTreeShaking(c.CmsComponentMenuComponent)
            );

            import(/* webpackChunkName: "cms-component-newsletter" */ './component/newsletter/cms-component-newsletter.component').then(c =>
                surviveTreeShaking(c.CmsComponentNewsletterComponent)
            );

            import(
                /* webpackChunkName: "cms-component-quick-links" */ './component/category-quick-links/cms-component-category-quick-links.component'
            ).then(c => surviveTreeShaking(c.CmsComponentCategoryQuickLinksComponent));

            import(
                /* webpackChunkName: "cms-component-row-accordion" */ './component/row-accordion/cms-component-row-accordion.component'
            ).then(c => surviveTreeShaking(c.CmsComponentRowAccordionComponent));

            import(/* webpackChunkName: "cms-component-row" */ './component/row/cms-component-row.component').then(c =>
                surviveTreeShaking(c.CmsComponentRowComponent)
            );

            import(/* webpackChunkName: "cms-component-text" */ './component/text/cms-component-text.component').then(c =>
                surviveTreeShaking(c.CmsComponentTextComponent)
            );

            import(/* webpackChunkName: "cms-component-video" */ './component/video/cms-component-video.component').then(c =>
                surviveTreeShaking(c.CmsComponentVideoComponent)
            );

            import(/* webpackChunkName: "cms-newsletter-block" */ './newsletter-block/cms-newsletter-block.component').then(c =>
                surviveTreeShaking(c.CmsNewsletterBlockComponent)
            );

            import(/* webpackChunkName: "cms-outlet-block" */ './outlet/block/cms-outlet-block.component').then(c =>
                surviveTreeShaking(c.CmsOutletBlockComponent)
            );

            import(/* webpackChunkName: "login-form-modal" */ '../login/form/modal/login-form-modal.component').then(c =>
                surviveTreeShaking(c.LoginFormModalComponent)
            );

            import(/* webpackChunkName: "multisite-modal" */ '../multisite/modal/multisite-modal.component').then(c =>
                surviveTreeShaking(c.MultisiteModalComponent)
            );

            import(/* webpackChunkName: "multisite-modal" */ '../multisite/us-info-modal/us-info-modal.component').then(c =>
                surviveTreeShaking(c.UsInfoModalComponent)
            );

            import(
                /* webpackChunkName: "page-component-2-images-with-copy" */ '../catalog/ui/page/component/2-images-with-copy/2-images-with-copy.component'
            ).then(c => surviveTreeShaking(c.PageComponent2ImagesWithCopyComponent));

            import(
                /* webpackChunkName: "page-component-blog-post-carousel" */ '../catalog/ui/page/component/blog-post-carousel/blog-post-carousel.component'
            ).then(c => surviveTreeShaking(c.PageComponentBlogPostCarouselComponent));

            import(
                /* webpackChunkName: "page-component-blog-post-carousel-v2" */ '../catalog/ui/page/component/blog-post-carousel-v2/blog-post-carousel-v2.component'
            ).then(c => surviveTreeShaking(c.PageComponentBlogPostCarouselV2Component));

            import(
                /* webpackChunkName: "page-component-category-carousel" */ '../catalog/ui/page/component/category-carousel/category-carousel.component'
            ).then(c => surviveTreeShaking(c.PageComponentCategoryCarouselComponent));

            import(
                /* webpackChunkName: "page-component-category-carousel-v2" */ '../catalog/ui/page/component/category-carousel-v2/category-carousel-v2.component'
            ).then(c => surviveTreeShaking(c.PageComponentCategoryCarouselV2Component));

            import(
                /* webpackChunkName: "page-component-countdown-banner" */ '../catalog/ui/product/countdown-banner/countdown-banner.component'
            ).then(c => surviveTreeShaking(c.PageComponentCountdownBannerComponent));

            import(
                /* webpackChunkName: "page-component-curalate-gallery" */ '../catalog/ui/page/component/curalate-gallery/curalate-gallery.component'
            ).then(c => surviveTreeShaking(c.PageComponentCuralateGalleryComponent));

            import(/* webpackChunkName: "page-component-faq" */ '../catalog/ui/page/component/faq/faq.component').then(c =>
                surviveTreeShaking(c.PageComponentFaqComponent)
            );

            import(
                /* webpackChunkName: "page-component-feature-banner" */ '../catalog/ui/page/component/feature-banner/feature-banner.component'
            ).then(c => surviveTreeShaking(c.PageComponentFeatureBannerComponent));

            import(
                /* webpackChunkName: "page-component-feature-banner-v2" */ '../catalog/ui/page/component/feature-banner-v2/feature-banner-v2.component'
            ).then(c => surviveTreeShaking(c.PageComponentFeatureBannerV2Component));

            import(
                /* webpackChunkName: "page-component-full-screen-hero-banner" */ '../catalog/ui/page/component/full-screen-hero-banner'
            ).then(c => surviveTreeShaking(c.PageComponentFullScreenHeroBannerComponent));

            import(
                /* webpackChunkName: "page-component-full-width-banner" */ '../catalog/ui/page/component/full-width-banner/full-width-banner.component'
            ).then(c => surviveTreeShaking(c.PageComponentFullWidthBannerComponent));

            import(
                /* webpackChunkName: "page-component-full-width-banner-v2" */ '../catalog/ui/page/component/full-width-banner-v2/full-width-banner-v2.component'
            ).then(c => surviveTreeShaking(c.PageComponentFullWidthBannerV2Component));

            import(
                /* webpackChunkName: "page-component-full-width-banner-v3" */ '../catalog/ui/page/component/full-width-banner-v3/full-width-banner-v3.component'
            ).then(c => surviveTreeShaking(c.PageComponentFullWidthBannerV3Component));

            import(/* webpackChunkName: "page-component-giftcards" */ '../catalog/ui/page/component/giftcards/giftcards.component').then(
                c => surviveTreeShaking(c.PageComponentGiftcardsComponent)
            );

            import(/* webpackChunkName: "page-component-iframe" */ '../catalog/ui/page/component/iframe/iframe.component').then(c =>
                surviveTreeShaking(c.PageComponentIframeComponent)
            );

            import(
                /* webpackChunkName: "page-component-image-with-copy-banner" */ '../catalog/ui/page/component/image-with-copy-banner/image-with-copy-banner.component'
            ).then(c => surviveTreeShaking(c.PageComponentImageWithCopyBannerComponent));

            import(
                /* webpackChunkName: "page-component-klarna-calculator" */ '../catalog/ui/page/component/klarna-calculator/klarna-calculator.component'
            ).then(c => surviveTreeShaking(c.PageComponentKlarnaCalculatorComponent));

            import(
                /* webpackChunkName: "page-component-multi-column-banner" */ '../catalog/ui/page/component/multi-column-banner/multi-column-banner.component'
            ).then(c => surviveTreeShaking(c.PageComponentMultiColumnBannerComponent));

            import(/* webpackChunkName: "page-component-multi-tab" */ '../catalog/ui/page/component/multi-tab/multi-tab.component').then(
                c => surviveTreeShaking(c.PageComponentMultiTabComponent)
            );

            import(
                /* webpackChunkName: "page-component-multi-column-banner-v2" */ '../catalog/ui/page/component/multi-column-banner-v2/multi-column-banner-v2.component'
            ).then(c => surviveTreeShaking(c.PageComponentMultiColumnBannerV2Component));

            import(
                /* webpackChunkName: "page-component-multi-column-banner-v3" */ '../catalog/ui/page/component/multi-column-banner-v3/multi-column-banner-v3.component'
            ).then(c => surviveTreeShaking(c.PageComponentMultiColumnBannerV3Component));

            import(
                /* webpackChunkName: "page-component-overlapped" */ '../catalog/ui/page/component/overlapped-component/overlapped-component.component'
            ).then(c => surviveTreeShaking(c.PageComponentOverlappedComponent));

            import(
                /* webpackChunkName: "page-component-product-carousel" */ '../catalog/ui/page/component/product-carousel/product-carousel.component'
            ).then(c => surviveTreeShaking(c.PageComponentProductCarouselComponent));

            import(
                /* webpackChunkName: "page-component-product-carousel-v2" */ '../catalog/ui/page/component/product-carousel-v2/product-carousel-v2.component'
            ).then(c => surviveTreeShaking(c.PageComponentProductCarouselV2Component));

            import(
                /* webpackChunkName: "page-component-quick-links" */ '../catalog/ui/page/component/quick-links/quick-links.component'
            ).then(c => surviveTreeShaking(c.PageComponentQuickLinksComponent));

            import(/* webpackChunkName: "page-component-recipe" */ '../catalog/ui/page/component/recipe/recipe.component').then(c =>
                surviveTreeShaking(c.PageComponentRecipeComponent)
            );

            import(
                /* webpackChunkName: "page-component-two-dimensional-carousel" */ '../catalog/ui/page/component/two-dimensional-carousel'
            ).then(c => surviveTreeShaking(c.PageComponentTwoDimensionalCarouselComponent));

            import(
                /* webpackChunkName: "page-component-size-guide-table" */ '../catalog/ui/page/component/size-guide-table/size-guide-table.component'
            ).then(c => surviveTreeShaking(c.PageComponentSizeGuideTableComponent));

            import(
                /* webpackChunkName: "page-component-social-links" */ '../catalog/ui/page/component/social-links/social-links.component'
            ).then(c => surviveTreeShaking(c.PageComponentSocialLinksComponent));

            import(/* webpackChunkName: "page-component-submenu" */ '../catalog/ui/page/component/submenu/submenu.component').then(c =>
                surviveTreeShaking(c.PageComponentSubmenuComponent)
            );

            import(
                /* webpackChunkName: "page-component-subscribe-newsletter" */ '../catalog/ui/page/component/subscribe-newsletter/subscribe-newsletter.component'
            ).then(c => surviveTreeShaking(c.PageComponentSubscribeNewsletterComponent));

            import(
                /* webpackChunkName: "product-view-usp-block" */ '../catalog/ui/product/view/usp-block/product-view-usp-block.component'
            ).then(c => surviveTreeShaking(c.ProductViewUspBlockComponent));

            import(
                /* webpackChunkName: "page-component-video-layout" */ '../catalog/ui/page/component/video-layout/video-layout.component'
            ).then(c => surviveTreeShaking(c.PageComponentVideoLayoutComponent));

            import(/* webpackChunkName: "page-store-finder" */ '../catalog/ui/page/component/store-finder/store-finder.component').then(c =>
                surviveTreeShaking(c.PageStoreFinderComponent)
            );

            import(
                /* webpackChunkName: "product-size-guide-modal" */ '../catalog/ui/product/size-guide/modal/product-size-guide-modal.component'
            ).then(c => surviveTreeShaking(c.ProductSizeGuideModalComponent));

            import(
                /* webpackChunkName: "recently-visited-products-carousel" */ './component/recently-visited-products-carousel/recently-visited-products-carousel.component'
            ).then(c => surviveTreeShaking(c.RecentlyVisitedProductsCarouselComponent));

            import(
                /* webpackChunkName: "related-products-carousel" */ '../catalog/ui/page/component/product-carousel/related-products-carousel.component'
            ).then(c => surviveTreeShaking(c.RelatedProductsCarouselComponent));

            import(/* webpackChunkName: "sign-up-popup-content" */ '../layout/page/sign-up-popup/sign-up-popup-content.component').then(c =>
                surviveTreeShaking(c.SignUpPopupContentComponent)
            );
        })
    ]);
}
